import { TrendingDown, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Schemas } from '../../shared/api/client';
import { formatRatio } from '../../shared/lib/format';
import { useLocale } from '../../shared/lib/i18n/use-locale';
import { Pill, StatusDot, Tooltip } from '../../shared/ui';

type Regime = Schemas['RegimeOut'];

export function TrendPill({ regime }: { regime: Regime }) {
  const { t } = useTranslation();
  if (!regime.trend) return null;
  const up = regime.trend === 'up';
  return (
    <Tooltip content={t('regime.trendHint')}>
      <span>
        <Pill icon={up ? <TrendingUp aria-hidden /> : <TrendingDown aria-hidden />}>
          {t(up ? 'regime.uptrend' : 'regime.downtrend')}
          {regime.trend_days !== null && (
            <span className="tabular text-text-3">{` · ${String(regime.trend_days)}`}</span>
          )}
        </Pill>
      </span>
    </Tooltip>
  );
}

const VOL_STATUS = { calm: 'good', normal: null, stressed: 'warning' } as const;

export function VolatilityPill({ regime }: { regime: Regime }) {
  const { t } = useTranslation();
  const locale = useLocale();
  if (!regime.vol_band || regime.volatility === null) return null;
  const status = VOL_STATUS[regime.vol_band];
  return (
    <Tooltip content={t('regime.volatilityHint')}>
      <span>
        <Pill icon={status ? <StatusDot status={status} /> : undefined}>
          {t(`regime.vol.${regime.vol_band}`)}
          <span className="tabular text-text-3">
            {formatRatio(regime.volatility, locale, { places: 0 })}
          </span>
        </Pill>
      </span>
    </Tooltip>
  );
}

export function RegimePills({ regime }: { regime: Regime | null }) {
  if (!regime) return null;
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <TrendPill regime={regime} />
      <VolatilityPill regime={regime} />
    </span>
  );
}
